import React from 'react';

import { COLORS } from '@constants/colors';

import { formatIndianNumber } from '@utils/numberFormatter';

interface NumberInputProps {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  prefix?: string;
  className?: string;
  disabled?: boolean;
  /** Whether to show a tooltip with formatted value on hover */
  showTooltip?: boolean;
  /** Custom tooltip content (overrides default formatted value) */
  tooltipContent?: string;
}

const NumberInput: React.FC<NumberInputProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  placeholder,
  prefix,
  className = '',
  disabled = false,
  showTooltip = false,
  tooltipContent,
}) => {
  // Get tooltip content
  const getTooltipContent = (): string => {
    if (tooltipContent) return tooltipContent;
    if (value && value > 0) {
      return `₹${formatIndianNumber(value)}`;
    }
    return '';
  };
  return (
    <div className={className || 'mb-8'}>
      <label
        className={`block text-sm font-semibold mb-3 ${disabled ? 'text-gray-400' : ''}`}
        style={{ color: disabled ? '#9CA3AF' : COLORS.charcoal }}
      >
        {label}
      </label>
      <div className={`relative ${showTooltip ? 'group' : ''}`}>
        {prefix && (
          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={value || ''}
          onChange={
            disabled
              ? undefined
              : e => {
                  const inputValue = e.target.value;
                  if (inputValue === '') {
                    onChange(null);
                  } else {
                    const numValue = Number(inputValue);
                    if (!isNaN(numValue)) {
                      // Allow typing without enforcing min/max constraints immediately
                      // Let the component handle validation on blur or form submission
                      onChange(numValue);
                    }
                  }
                }
          }
          onBlur={
            disabled
              ? undefined
              : e => {
                  // Enforce min/max constraints on blur
                  const inputValue = e.target.value;
                  if (inputValue !== '') {
                    const numValue = Number(inputValue);
                    if (!isNaN(numValue)) {
                      let finalValue = numValue;
                      if (min !== undefined)
                        finalValue = Math.max(min, finalValue);
                      if (max !== undefined)
                        finalValue = Math.min(max, finalValue);
                      if (finalValue !== numValue) {
                        onChange(finalValue);
                      }
                    }
                  }
                }
          }
          onWheel={e => {
            // Prevent scroll from changing the number value
            // This fixes the confusing behavior where users try to scroll the page
            // but accidentally increment/decrement the input value
            e.currentTarget.blur();
          }}
          className={`w-full ${prefix ? 'pl-8' : 'pl-4'} pr-4 py-4 border rounded-xl text-lg font-medium ${
            disabled
              ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
              : 'border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          }`}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          disabled={disabled}
        />
        {/* Tooltip */}
        {showTooltip && getTooltipContent() && (
          <div className="invisible group-hover:visible absolute z-10 px-3 py-2 text-sm text-white bg-gray-800 rounded-lg shadow-lg -top-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            {getTooltipContent()}
            {/* Arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberInput;
